import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { db } from "../../firebase";
import {
  collection, doc, addDoc, updateDoc,
  query, where, onSnapshot, serverTimestamp, arrayRemove,
} from "firebase/firestore";
import { IC, Avatar, Stars, Pill, Btn, PlaceAutocomplete, useToast } from "../shared";

// ── Google Maps bootstrap (uses env var instead of hardcoded key) ─────────────
((g)=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=`https://maps.googleapis.com/maps/api/js?`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({
  key: process.env.REACT_APP_GOOGLE_MAPS_KEY,
  v: "weekly",
});

// ── Route Selector (driver picks from Google Maps alternatives) ──────────────
export const RouteSelector = ({ fromCoords, toCoords, waypoints, onChange }) => {
  const mapRef         = useRef(null);
  const mapInstanceRef = useRef(null);
  const polylinesRef   = useRef([]);
  const markersRef     = useRef([]);
  const onChangeRef    = useRef(onChange);
  onChangeRef.current  = onChange;
  const [routes,      setRoutes]      = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [loading,     setLoading]     = useState(false);
  const [failed,      setFailed]      = useState(false);
  const wpsKey = JSON.stringify(waypoints || []);
  const [failReason, setFailReason] = useState('');

  const fetchRoutes = async () => {
    if (!fromCoords || !toCoords) { setRoutes([]); onChangeRef.current(null); return; }
    try { await google.maps.importLibrary("routes"); } catch { setFailReason('Maps API not loaded'); setFailed(true); return; }
    const gm = google.maps;
    if (typeof gm.DirectionsService !== 'function') {
      setFailReason('DirectionsService unavailable'); setFailed(true); return;
    }
    setLoading(true); setFailed(false); setFailReason('');
    try {
      const wps = (waypoints || []).filter(w => w.lat && w.lon).map(w => ({
        location: new gm.LatLng(w.lat, w.lon), stopover: true,
      }));
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout')), 12000)
      );
      const result = await Promise.race([
        new gm.DirectionsService().route({
          origin:                   new gm.LatLng(fromCoords.lat, fromCoords.lon),
          destination:              new gm.LatLng(toCoords.lat,   toCoords.lon),
          waypoints:                wps,
          provideRouteAlternatives: true,
          travelMode:               gm.TravelMode.DRIVING,
        }),
        timeout,
      ]);
      if (!result.routes?.length) {
        setFailReason('No route found between these locations');
        setFailed(true);
        return;
      }
      const parsed = result.routes.map(r => ({
        coords:      r.overview_path.map(p => ({ lat: p.lat(), lng: p.lng() })),
        distanceKm:  r.legs.reduce((s, l) => s + l.distance.value, 0) / 1000,
        durationMin: Math.round(r.legs.reduce((s, l) => s + l.duration.value, 0) / 60),
      }));
      setRoutes(parsed);
      setSelectedIdx(0);
      onChangeRef.current(parsed[0]);
    } catch (e) {
      const reason = e?.code || e?.message || String(e);
      console.error('DirectionsService error:', reason, e);
      setFailReason(reason === 'ZERO_RESULTS' ? 'No route found between these locations' : reason === 'NOT_FOUND' ? 'Location not found — please try again' : reason);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchRoutes(); }, [fromCoords?.lat, fromCoords?.lon, toCoords?.lat, toCoords?.lon, wpsKey]);

  // Ensure map exists and is centered appropriately
  useEffect(() => {
    if (!mapRef.current) return;
    let cancelled = false;
    (async () => {
      try { await google.maps.importLibrary("maps"); } catch { return; }
      if (cancelled) return;
      const gm = google.maps;
      if (!mapInstanceRef.current) {
        mapInstanceRef.current = new gm.Map(mapRef.current, { mapId: 'igo_route_map', disableDefaultUI: true, gestureHandling: 'greedy', center: { lat: 34.9, lng: 33.1 }, zoom: 9 });
      }
      const map = mapInstanceRef.current;
      if (!fromCoords && !toCoords) {
        // No locations — default Cyprus view
        map.setCenter({ lat: 34.9, lng: 33.1 });
        map.setZoom(9);
      } else if (fromCoords && !toCoords) {
        map.setCenter({ lat: fromCoords.lat, lng: fromCoords.lon });
        map.setZoom(12);
      } else if (!fromCoords && toCoords) {
        map.setCenter({ lat: toCoords.lat, lng: toCoords.lon });
        map.setZoom(12);
      }
      // When both coords are set, the route draw effect handles fitBounds
    })();
    return () => { cancelled = true; };
  }, [fromCoords, toCoords]);

  // Draw routes whenever routes or selection changes
  useEffect(() => {
    if (!routes.length || !mapRef.current) return;
    let cancelled = false;
    let listener;
    (async () => {
      await google.maps.importLibrary("maps");
      await google.maps.importLibrary("marker");
      if (cancelled) return;
      const gm = google.maps;

      // Clear previous overlays
      polylinesRef.current.forEach(p => p.setMap(null)); polylinesRef.current = [];
      markersRef.current.forEach(m => { m.map = null; }); markersRef.current = [];

      if (!mapInstanceRef.current) {
        mapInstanceRef.current = new gm.Map(mapRef.current, { mapId: 'igo_route_map', disableDefaultUI: true, gestureHandling: 'greedy', center: { lat: 34.9, lng: 33.1 }, zoom: 9 });
      }
      const map = mapInstanceRef.current;
      const bounds = new gm.LatLngBounds();

      const mkMarker = (lat, lng, color) => {
        const dot = document.createElement('div');
        dot.style.cssText = `width:14px;height:14px;border-radius:50%;background:${color};border:2.5px solid #fff;box-sizing:content-box;`;
        const m = new gm.marker.AdvancedMarkerElement({ position: { lat, lng }, map, content: dot });
        markersRef.current.push(m); bounds.extend({ lat, lng });
      };

      // Draw unselected alternatives first (grey, behind)
      routes.forEach((r, i) => {
        if (i === selectedIdx) return;
        const path = r.coords;
        const poly = new gm.Polyline({ path, map, strokeColor: '#94a3b8', strokeWeight: 4, strokeOpacity: 0.55, clickable: true });
        poly.addListener('click', () => { setSelectedIdx(i); onChangeRef.current(routes[i]); });
        polylinesRef.current.push(poly);
        path.forEach(p => bounds.extend(p));
      });
      // Draw selected route on top (blue)
      const sel = routes[selectedIdx];
      const selPath = sel.coords;
      const mainPoly = new gm.Polyline({ path: selPath, map, strokeColor: '#2b5be0', strokeWeight: 5, strokeOpacity: 0.85 });
      polylinesRef.current.push(mainPoly);
      selPath.forEach(p => bounds.extend(p));

      mkMarker(fromCoords.lat, fromCoords.lon, '#2b5be0');
      mkMarker(toCoords.lat,   toCoords.lon,   '#1a3db5');
      (waypoints || []).filter(w => w.lat && w.lon).forEach(w => mkMarker(w.lat, w.lon, '#9b59b6'));
      // Fit bounds with 50px padding, re-fit after idle to handle container resize
      map.fitBounds(bounds, 50);
      listener = gm.event.addListenerOnce(map, 'idle', () => map.fitBounds(bounds, 50));
    })();
    return () => { cancelled = true; if (listener) google.maps.event.removeListener(listener); };
  }, [routes, selectedIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear overlays when coords become incomplete
  useEffect(() => {
    if (!fromCoords || !toCoords) {
      polylinesRef.current.forEach(p => p.setMap(null)); polylinesRef.current = [];
      markersRef.current.forEach(m => { m.map = null; }); markersRef.current = [];
    }
  }, [fromCoords, toCoords]);

  // Cleanup on unmount
  useEffect(() => () => {
    polylinesRef.current.forEach(p => p.setMap(null));
    markersRef.current.forEach(m => { m.map = null; });
    mapInstanceRef.current = null;
  }, []);

  const hasCoords = fromCoords && toCoords;

  return (
    <div>
      {/* Map always visible — defaults to Cyprus when no coords */}
      <div ref={mapRef} className="route-map" style={{ height: 220, borderRadius: 16, overflow: 'hidden', border: '1.5px solid var(--border)' }}/>
      {hasCoords && !loading && (failed || !routes.length) && (
        <div style={{ height: 160, borderRadius: 16, border: '1.5px solid var(--border)', background: 'var(--warm)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>{failReason || "Couldn't load route"}</span>
          <button onClick={fetchRoutes} style={{ fontSize: 12, color: 'var(--accent)', fontFamily: 'Syne', fontWeight: 700, background: 'var(--card)', border: '1.5px solid var(--border)', borderRadius: 10, padding: '6px 16px', cursor: 'pointer' }}>Try again</button>
        </div>
      )}
      {hasCoords && routes.length > 1 && !loading && !failed && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          {routes.map((r, i) => (
            <button key={i} onClick={() => { setSelectedIdx(i); onChangeRef.current(r); }} style={{
              flex: 1, padding: '10px 8px', borderRadius: 12,
              border: `1.5px solid ${i === selectedIdx ? 'var(--accent)' : 'var(--border)'}`,
              background: i === selectedIdx ? 'rgba(43,91,224,.08)' : 'var(--card)',
              fontFamily: 'Syne', fontWeight: 700, fontSize: 12, lineHeight: 1.5,
              color: i === selectedIdx ? 'var(--accent)' : 'var(--muted)', cursor: 'pointer', transition: 'all .15s',
            }}>
              Route {i + 1}<br/>
              <span style={{ fontWeight: 400, fontSize: 11 }}>{r.distanceKm.toFixed(1)} km · {r.durationMin} min</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Route Map (display only — uses stored geometry or fetches fresh) ───────────
export const RouteMap = ({ trip, passengerFromCoords, passengerToCoords, waypoints }) => {
  const mapRef         = useRef(null);
  const mapInstanceRef = useRef(null);
  const polylinesRef   = useRef([]);
  const markersRef     = useRef([]);
  const wpsKey = JSON.stringify(waypoints || []);

  useEffect(() => {
    if (!trip.fromLat || !mapRef.current) return;
    let cancelled = false;
    (async () => {
      await google.maps.importLibrary("maps");
      await google.maps.importLibrary("marker");
      await google.maps.importLibrary("routes");
      if (cancelled) return;
      const gm = google.maps;

      // Clear previous overlays
      polylinesRef.current.forEach(p => p.setMap(null)); polylinesRef.current = [];
      markersRef.current.forEach(m => { m.map = null; }); markersRef.current = [];

      if (!mapInstanceRef.current) {
        mapInstanceRef.current = new gm.Map(mapRef.current, { mapId: 'igo_route_map', disableDefaultUI: true, gestureHandling: 'greedy', center: { lat: 34.9, lng: 33.1 }, zoom: 9 });
      }
      const map = mapInstanceRef.current;
      const bounds = new gm.LatLngBounds();

      const mkMarker = (lat, lng, color) => {
        const dot = document.createElement('div');
        dot.style.cssText = `width:14px;height:14px;border-radius:50%;background:${color};border:2.5px solid #fff;box-sizing:content-box;`;
        const m = new gm.marker.AdvancedMarkerElement({ position: { lat, lng }, map, content: dot });
        markersRef.current.push(m); bounds.extend({ lat, lng });
      };

      const drawMain = (coords) => {
        const wps = (waypoints || []).filter(w => w.lat && w.lon);
        // Support both old [[lat,lng]] format and new {lat,lng} format
        const path = coords.map(c => Array.isArray(c) ? { lat: c[0], lng: c[1] } : c);
        const poly = new gm.Polyline({ path, map, strokeColor: '#2b5be0', strokeWeight: 5, strokeOpacity: 0.7 });
        polylinesRef.current.push(poly);
        path.forEach(p => bounds.extend(p));

        mkMarker(trip.fromLat, trip.fromLon, '#2b5be0');
        mkMarker(trip.toLat,   trip.toLon,   '#1a3db5');
        wps.forEach(w => mkMarker(w.lat, w.lon, '#9b59b6'));

        if (passengerFromCoords && passengerToCoords) {
          // Find closest points on driver's route to passenger pickup/dropoff
          const findClosestIdx = (lat, lon, pathArr) => {
            let minDist = Infinity, idx = 0;
            pathArr.forEach((p, i) => {
              const d = (p.lat - lat) ** 2 + ((p.lng ?? p.lon) - lon) ** 2;
              if (d < minDist) { minDist = d; idx = i; }
            });
            return idx;
          };
          const startIdx = findClosestIdx(passengerFromCoords.lat, passengerFromCoords.lon, path);
          const endIdx   = findClosestIdx(passengerToCoords.lat,   passengerToCoords.lon,   path);
          const lo = Math.min(startIdx, endIdx);
          const hi = Math.max(startIdx, endIdx);
          if (hi > lo) {
            const segPath = path.slice(lo, hi + 1);
            const segPoly = new gm.Polyline({ path: segPath, map, strokeColor: '#f5a623', strokeWeight: 5, strokeOpacity: 0.95 });
            polylinesRef.current.push(segPoly);
          }
          mkMarker(passengerFromCoords.lat, passengerFromCoords.lon, '#f5a623');
          mkMarker(passengerToCoords.lat,   passengerToCoords.lon,   '#e67e22');
        }
        map.fitBounds(bounds, 50);
        gm.event.addListenerOnce(map, 'idle', () => map.fitBounds(bounds, 50));
      };

      if (trip.routeGeometry?.length) {
        drawMain(trip.routeGeometry);
      } else {
        const wps = (waypoints || []).filter(w => w.lat && w.lon);
        try {
          new gm.DirectionsService().route({
            origin:      new gm.LatLng(trip.fromLat, trip.fromLon),
            destination: new gm.LatLng(trip.toLat,   trip.toLon),
            waypoints:   wps.map(w => ({ location: new gm.LatLng(w.lat, w.lon), stopover: true })),
            travelMode:  gm.TravelMode.DRIVING,
          }, (result, status) => {
            if (status !== 'OK' || !mapInstanceRef.current) return;
            drawMain(result.routes[0].overview_path.map(p => ({ lat: p.lat(), lng: p.lng() })));
          });
        } catch (e) { console.warn('Route map fetch error:', e); }
      }
    })();
    return () => {
      cancelled = true;
      polylinesRef.current.forEach(p => p.setMap(null));
      markersRef.current.forEach(m => { m.map = null; });
    };
  }, [trip.fromLat, trip.fromLon, trip.toLat, trip.toLon, trip.routeGeometry, // eslint-disable-line react-hooks/exhaustive-deps
      passengerFromCoords?.lat, passengerFromCoords?.lon, passengerToCoords?.lat, passengerToCoords?.lon, wpsKey]);

  // Cleanup on unmount
  useEffect(() => () => {
    polylinesRef.current.forEach(p => p.setMap(null));
    markersRef.current.forEach(m => { m.map = null; });
    mapInstanceRef.current = null;
  }, []);

  if (!trip.fromLat) return null;
  return <div ref={mapRef} className="route-map" style={{ height: 220, borderRadius: 16, overflow: 'hidden', border: '1.5px solid var(--border)' }}/>;
};

// ── Trip Detail ───────────────────────────────────────────────────────────────
const TripDetail = ({ trip, currentUser, onBack }) => {
  const toast = useToast();
  const [myReq,            setMyReq]            = useState(null);
  const [requesting,       setRequesting]       = useState(false);
  const [showStops,        setShowStops]        = useState(false);
  const [pFrom,            setPFrom]            = useState(trip.from);
  const [pTo,              setPTo]              = useState(trip.to);
  const [pFromCoords,      setPFromCoords]      = useState(trip.fromLat ? { lat: trip.fromLat, lon: trip.fromLon } : null);
  const [pToCoords,        setPToCoords]        = useState(trip.toLat   ? { lat: trip.toLat,   lon: trip.toLon   } : null);
  const [segmentPrice,     setSegmentPrice]     = useState(trip.price);
  const [calcingPrice,     setCalcingPrice]     = useState(false);
  const [cancelConfirm,    setCancelConfirm]    = useState(false);
  const [cancelling,       setCancelling]       = useState(false);

  const driver    = { id: trip.driverId, name: trip.driverName, avatar: trip.driverAvatar, rating: trip.driverRating || 0 };
  const seatsLeft = trip.seats - (trip.passengers?.length || 0);
  const isDriver  = trip.driverId === currentUser.id;
  const dt        = new Date(trip.datetime);
  const isPitStop = pFrom !== trip.from || pTo !== trip.to;

  useEffect(() => {
    const q = query(collection(db, "requests"), where("tripId", "==", trip.id), where("passengerId", "==", currentUser.id));
    const unsub = onSnapshot(q, snap => {
      if (snap.empty) { setMyReq(null); return; }
      // Prefer the most recent active request (pending/accepted), fall back to latest cancelled/rejected
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const active = docs.find(d => d.status === "pending" || d.status === "accepted");
      setMyReq(active || docs[docs.length - 1]);
    });
    return unsub;
  }, [trip.id, currentUser.id]);

  // Recalculate price when passenger changes their stops
  useEffect(() => {
    if (!pFromCoords || !pToCoords || !trip.distanceKm) { setSegmentPrice(trip.price); return; }
    if (!isPitStop) { setSegmentPrice(trip.price); return; }
    setCalcingPrice(true);
    const controller = new AbortController();
    const url = `https://router.project-osrm.org/route/v1/driving/${pFromCoords.lon},${pFromCoords.lat};${pToCoords.lon},${pToCoords.lat}?overview=false`;
    fetch(url, { signal: controller.signal }).then(r => r.json()).then(data => {
      if (data.routes?.[0]) {
        const segKm = data.routes[0].distance / 1000;
        const ratio = Math.min(segKm / trip.distanceKm, 1);
        setSegmentPrice(Math.max(0.5, parseFloat((ratio * trip.price).toFixed(2))));
      }
    }).catch(() => {}).finally(() => setCalcingPrice(false));
    return () => controller.abort();
  }, [pFromCoords, pToCoords, isPitStop, trip.price, trip.distanceKm]);

  const requestSeat = async () => {
    const hasActiveReq = myReq && myReq.status !== "cancelled" && myReq.status !== "rejected";
    if (hasActiveReq || isDriver || seatsLeft === 0) return;
    setRequesting(true);
    try {
      // If there's an old cancelled/rejected request, mark it cancelled and create a fresh one
      if (myReq && (myReq.status === "cancelled" || myReq.status === "rejected")) {
        await updateDoc(doc(db, "requests", myReq.id), { status: "cancelled" });
      }
      await addDoc(collection(db, "requests"), {
        tripId: trip.id, passengerId: currentUser.id,
        passengerName: currentUser.name, passengerAvatar: currentUser.avatar,
        passengerRating: currentUser.rating || 0, passengerRatingCount: currentUser.ratingCount || 0,
        driverId: trip.driverId, driverName: trip.driverName, driverAvatar: trip.driverAvatar,
        driverRating: trip.driverRating || 0,
        tripFrom: trip.from, tripTo: trip.to, tripDatetime: trip.datetime, tripPrice: trip.price,
        pickupFrom: pFrom, dropoffTo: pTo,
        pickupFromLat: pFromCoords?.lat || null, pickupFromLon: pFromCoords?.lon || null,
        dropoffToLat:  pToCoords?.lat   || null, dropoffToLon:  pToCoords?.lon   || null,
        segmentPrice, isPitStop,
        status: "pending", createdAt: serverTimestamp(),
      });
      await addDoc(collection(db, "notifications", trip.driverId, "items"), {
        text: isPitStop
          ? `${currentUser.name} requested a seat on your trip ${trip.from} → ${trip.to}, boarding at ${pFrom} and exiting at ${pTo}.`
          : `${currentUser.name} requested a seat on your trip to ${trip.to}.`,
        tab: "trips", subview: "driving", type: "request",
        ts: serverTimestamp(), read: false,
      });
      toast("Seat requested! Waiting for driver to respond.", "success");
    } catch (e) { console.error(e); toast("Failed to send request. Please try again.", "error"); }
    setRequesting(false);
  };

  const displayPrice = calcingPrice ? "…" : `€${segmentPrice}`;

  const bookingPanel = (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Driver card */}
      <div style={{ background: "var(--card)", borderRadius: 20, padding: 18, border: "1.5px solid var(--border)", boxShadow: "var(--shadow)" }}>
        <div style={{ fontSize: 11, fontFamily: "Syne", fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 12 }}>Driver</div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <Avatar user={driver} size={52}/>
          <div>
            <div style={{ fontWeight: 700, fontFamily: "Syne", fontSize: 17, color: "var(--ink)" }}>{driver.name}</div>
            <Stars rating={driver.rating}/>
          </div>
        </div>
      </div>

      {/* Price summary */}
      <div style={{ background: "var(--card)", borderRadius: 20, padding: 18, border: "1.5px solid var(--border)", boxShadow: "var(--shadow)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <span style={{ fontSize: 14, color: "var(--muted)" }}>{isPitStop ? "Your segment" : "Price per seat"}</span>
          <span style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 24, color: "var(--accent)" }}>{displayPrice}</span>
        </div>
        <div style={{ display: "flex", gap: 12, fontSize: 13, color: "var(--muted)" }}>
          <span>{seatsLeft} seat{seatsLeft !== 1 ? "s" : ""} left</span>
          <span>·</span>
          <span>{dt.toLocaleDateString("en",{month:"short",day:"numeric"})} at {dt.toLocaleTimeString("en",{hour:"2-digit",minute:"2-digit"})}</span>
        </div>
      </div>

      {/* Pit stop customisation */}
      {!isDriver && !myReq && seatsLeft > 0 && trip.fromLat && (
        <div style={{ background: "var(--card)", borderRadius: 20, border: "1.5px solid var(--border)", boxShadow: "var(--shadow)", overflow: "hidden" }}>
          <button onClick={() => setShowStops(s => !s)} style={{ width: "100%", padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", background: "none", border: "none", cursor: "pointer" }}>
            <div>
              <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 14, color: "var(--ink)", textAlign: "left" }}>Customize my stops</div>
              <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>Get on or off at a different point</div>
            </div>
            <span style={{ fontSize: 18, color: "var(--muted)", transform: showStops ? "rotate(180deg)" : "none", transition: "transform .2s" }}>›</span>
          </button>
          {showStops && (
            <div style={{ padding: "0 18px 16px", display: "flex", flexDirection: "column", gap: 10, borderTop: "1px solid var(--border)" }}>
              <div style={{ marginTop: 12 }}>
                <PlaceAutocomplete label="Your pickup" placeholder={trip.from} value={pFrom} onChange={v => { setPFrom(v); setPFromCoords(null); }} onCoords={setPFromCoords}/>
              </div>
              <PlaceAutocomplete label="Your dropoff" placeholder={trip.to} value={pTo} onChange={v => { setPTo(v); setPToCoords(null); }} onCoords={setPToCoords}/>
              {isPitStop && (
                <div style={{ background: "var(--warm)", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "var(--ink)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ color: "var(--muted)" }}>Your segment price</span>
                  <span style={{ fontFamily: "Syne", fontWeight: 800, color: "var(--accent)", fontSize: 16 }}>{calcingPrice ? "Calculating…" : `€${segmentPrice}`}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Status / CTA */}
      {!isDriver && (
        myReq && myReq.status === "accepted" ? (
          <div style={{ display: "flex", gap: 10 }}>
            <Btn variant="success" disabled style={{ flex: 1, opacity: 1 }}>
              <span style={{ width: 16, height: 16 }}>{IC.check}</span>
              Seat Confirmed
            </Btn>
            <Btn variant="danger" onClick={() => setCancelConfirm(true)} style={{ flex: 0, width: "auto", padding: "13px 18px" }}>
              Cancel
            </Btn>
          </div>
        ) : myReq && myReq.status === "pending" ? (
          <Btn variant="danger" onClick={() => setCancelConfirm(true)}>
            Cancel Request
          </Btn>
        ) : myReq && myReq.status === "rejected" ? (
          seatsLeft > 0 ? (
            <Btn variant="accent" onClick={requestSeat} disabled={requesting || calcingPrice}>
              <span style={{ width: 16, height: 16 }}>{IC.seat}</span>
              {requesting ? "Sending…" : "Request Again"}
            </Btn>
          ) : (
            <Btn variant="ghost" disabled style={{ opacity: 0.6 }}>Driver Declined</Btn>
          )
        ) : seatsLeft > 0 ? (
          <Btn variant="accent" onClick={requestSeat} disabled={requesting || calcingPrice}>
            <span style={{ width: 16, height: 16 }}>{IC.seat}</span>
            {requesting ? "Sending…" : isPitStop ? `Request Seat — €${segmentPrice}` : "Request a Seat"}
          </Btn>
        ) : (
          <Btn variant="ghost" disabled>No Seats Available</Btn>
        )
      )}
    </div>
  );

  return (
    <>
    {/* ── Mobile: bottom-sheet overlay ── */}
    <div className="mobile-only" style={{ position: "absolute", inset: 0, zIndex: 200 }}>
      {/* Backdrop */}
      <div onClick={onBack} style={{ position: "absolute", inset: 0, background: "rgba(15,14,19,0.5)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", animation: "fadeIn .25s ease both" }}/>
      {/* Sheet */}
      <div className="asheet-up" style={{ position: "absolute", bottom: 0, left: 0, right: 0, maxHeight: "85%", background: "var(--card)", borderRadius: "28px 28px 0 0", boxShadow: "0 -10px 40px rgba(0,0,0,.15)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Handle + close */}
        <div style={{ flexShrink: 0, padding: "12px 20px 0", position: "relative" }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: "var(--border)", margin: "0 auto 8px" }}/>
          <button onClick={onBack} style={{ position: "absolute", top: 10, right: 16, width: 32, height: 32, borderRadius: "50%", background: "rgba(192,57,43,0.15)", display: "flex", alignItems: "center", justifyContent: "center", border: "none", cursor: "pointer", zIndex: 10 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#c0392b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "0 20px 24px", WebkitOverflowScrolling: "touch" }}>
          {/* Map at top */}
          <div style={{ borderRadius: 16, overflow: "hidden", marginBottom: 16, border: "1.5px solid var(--border)" }}>
            <RouteMap trip={trip} waypoints={trip.waypoints} passengerFromCoords={isPitStop ? pFromCoords : null} passengerToCoords={isPitStop ? pToCoords : null}/>
          </div>

          {/* Title */}
          <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 18, color: "var(--ink)", marginBottom: 16 }}>Trip Details</div>

          {/* Pickup */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--accent)", boxShadow: "0 0 0 3px rgba(43,91,224,.15)", marginTop: 4, flexShrink: 0 }}/>
            <div>
              <div style={{ fontSize: 10, fontFamily: "Syne", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Pickup</div>
              <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 18, color: "var(--ink)" }}>{trip.from}</div>
              <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
                {dt.toLocaleDateString("en",{month:"short",day:"numeric"})} at {dt.toLocaleTimeString("en",{hour:"2-digit",minute:"2-digit"})}
              </div>
            </div>
          </div>
          {/* Connecting line */}
          <div style={{ width: 1.5, height: 20, background: "var(--border)", marginLeft: 4 }}/>
          {/* Destination */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 4, marginBottom: 20 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: "var(--accent2)", boxShadow: "0 0 0 3px rgba(43,91,224,.15)", marginTop: 4, flexShrink: 0 }}/>
            <div>
              <div style={{ fontSize: 10, fontFamily: "Syne", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Destination</div>
              <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 18, color: "var(--ink)" }}>{trip.to}</div>
            </div>
          </div>

          {/* 2x2 info grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
            <div style={{ background: "var(--paper)", borderRadius: 14, padding: 14 }}>
              <div style={{ fontSize: 10, fontFamily: "Syne", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Price</div>
              <div style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 24, color: "var(--accent)" }}>{displayPrice}</div>
            </div>
            <div style={{ background: "var(--paper)", borderRadius: 14, padding: 14 }}>
              <div style={{ fontSize: 10, fontFamily: "Syne", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Available</div>
              <div style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 24, color: "var(--ink)" }}>{seatsLeft} seat{seatsLeft !== 1 ? "s" : ""}</div>
            </div>
            <div style={{ background: "var(--paper)", borderRadius: 14, padding: 14 }}>
              <div style={{ fontSize: 10, fontFamily: "Syne", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Distance</div>
              <div style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 24, color: "var(--ink)" }}>{trip.distanceKm ? `${Math.round(trip.distanceKm)} km` : "—"}</div>
            </div>
            <div style={{ background: "var(--paper)", borderRadius: 14, padding: 14 }}>
              <div style={{ fontSize: 10, fontFamily: "Syne", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Notes</div>
              <div style={{ fontWeight: 600, fontSize: 14, color: trip.notes ? "var(--ink)" : "var(--muted)" }}>{trip.notes || "None"}</div>
            </div>
          </div>

          {/* Driver card */}
          <div style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 16, padding: 14, display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <Avatar user={driver} size={44}/>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 15, color: "var(--ink)" }}>{driver.name}</div>
              <Stars rating={driver.rating}/>
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>{trip.driverRatingCount || 0} trips completed</div>
            </div>
          </div>

          {/* Pit stop customisation */}
          {!isDriver && (!myReq || myReq.status === "cancelled" || myReq.status === "rejected") && seatsLeft > 0 && trip.fromLat && (
            <div style={{ background: "var(--card)", borderRadius: 16, border: "1.5px solid var(--border)", overflow: "hidden", marginBottom: 16 }}>
              <button onClick={() => setShowStops(s => !s)} style={{ width: "100%", padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", background: "none", border: "none", cursor: "pointer" }}>
                <div>
                  <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 14, color: "var(--ink)", textAlign: "left" }}>Customize my stops</div>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>Get on or off at a different point</div>
                </div>
                <span style={{ fontSize: 18, color: "var(--muted)", transform: showStops ? "rotate(180deg)" : "none", transition: "transform .2s" }}>›</span>
              </button>
              {showStops && (
                <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 10, borderTop: "1px solid var(--border)" }}>
                  <div style={{ marginTop: 12 }}>
                    <PlaceAutocomplete label="Your pickup" placeholder={trip.from} value={pFrom} onChange={v => { setPFrom(v); setPFromCoords(null); }} onCoords={setPFromCoords}/>
                  </div>
                  <PlaceAutocomplete label="Your dropoff" placeholder={trip.to} value={pTo} onChange={v => { setPTo(v); setPToCoords(null); }} onCoords={setPToCoords}/>
                  {isPitStop && (
                    <div style={{ background: "var(--warm)", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "var(--ink)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ color: "var(--muted)" }}>Your segment price</span>
                      <span style={{ fontFamily: "Syne", fontWeight: 800, color: "var(--accent)", fontSize: 16 }}>{calcingPrice ? "Calculating…" : `€${segmentPrice}`}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Status / CTA */}
          {!isDriver && (
            myReq && myReq.status === "accepted" ? (
              <div style={{ display: "flex", gap: 10 }}>
                <Btn variant="success" disabled style={{ flex: 1, opacity: 1 }}>
                  <span style={{ width: 16, height: 16 }}>{IC.check}</span>
                  Seat Confirmed
                </Btn>
                <Btn variant="danger" onClick={() => setCancelConfirm(true)} style={{ flex: 0, width: "auto", padding: "13px 18px" }}>
                  Cancel
                </Btn>
              </div>
            ) : myReq && myReq.status === "pending" ? (
              <Btn variant="danger" onClick={() => setCancelConfirm(true)}>
                Cancel Request
              </Btn>
            ) : myReq && myReq.status === "rejected" ? (
              <Btn variant="ghost" disabled style={{ opacity: 0.6 }}>
                Driver Declined
              </Btn>
            ) : seatsLeft > 0 ? (
              <button onClick={requestSeat} disabled={requesting || calcingPrice} style={{ width: "100%", padding: 14, background: "var(--accent)", color: "#fff", borderRadius: 14, fontFamily: "Syne", fontWeight: 700, fontSize: 14, border: "none", cursor: "pointer", boxShadow: "0 4px 16px rgba(43,91,224,.25)", transition: "all .2s", opacity: requesting || calcingPrice ? 0.5 : 1 }}>
                {requesting ? "Sending…" : `Request Seat · ${displayPrice}`}
              </button>
            ) : (
              <Btn variant="ghost" disabled>No Seats Available</Btn>
            )
          )}
        </div>
      </div>
    </div>

    {/* ── Desktop layout (unchanged) ── */}
    <div className="screen aslide desk-only" style={{ paddingBottom: 0 }}>

      {/* ── Desktop layout ── */}
      <div className="desk-only">
        {/* Desktop sub-header */}
        <div style={{ background: "var(--card)", borderBottom: "1.5px solid var(--border)", padding: "16px 0" }}>
          <div className="desk-container" style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--muted)", fontFamily: "Syne", fontWeight: 600, fontSize: 14, background: "none", border: "none", cursor: "pointer", padding: "6px 0" }}>
              <span style={{ width: 16, height: 16 }}>{IC.back}</span> Back
            </button>
            <div style={{ flex: 1 }}>
              <span style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 20, color: "var(--ink)" }}>{trip.from}</span>
              <span style={{ color: "var(--muted)", margin: "0 10px", fontSize: 18 }}>→</span>
              <span style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 20, color: "var(--ink)" }}>{trip.to}</span>
            </div>
            <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 13, color: "var(--muted)" }}>
              {dt.toLocaleDateString("en",{weekday:"short",month:"short",day:"numeric"})} · {dt.toLocaleTimeString("en",{hour:"2-digit",minute:"2-digit"})}
            </div>
          </div>
        </div>
        {/* Two-column layout */}
        <div className="desk-detail-layout">
          <div className="desk-detail-left">
            <RouteMap trip={trip} waypoints={trip.waypoints} passengerFromCoords={isPitStop ? pFromCoords : null} passengerToCoords={isPitStop ? pToCoords : null}/>
            <div style={{ background: "var(--card)", borderRadius: 20, padding: 24, border: "1.5px solid var(--border)", boxShadow: "var(--shadow)" }}>
              <div style={{ fontSize: 11, fontFamily: "Syne", fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 16 }}>Route</div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0, paddingTop: 4 }}>
                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--accent)", boxShadow: "0 0 0 3px rgba(43,91,224,.15)" }}/>
                  <div style={{ width: 2, height: 32, background: "var(--border)", margin: "4px 0" }}/>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: "var(--accent2)", boxShadow: "0 0 0 3px rgba(43,91,224,.1)" }}/>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 16, color: "var(--ink)", marginBottom: 28 }}>{trip.from}</div>
                  <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 16, color: "var(--ink)" }}>{trip.to}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 28 }}>Departure</div>
                  <div style={{ fontSize: 13, color: "var(--muted)" }}>Arrival</div>
                </div>
              </div>
            </div>
          </div>
          <div className="desk-detail-right">
            {bookingPanel}
          </div>
        </div>
      </div>
    </div>

    {cancelConfirm && myReq && createPortal(
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-end", zIndex: 1000, paddingBottom: "calc(80px + env(safe-area-inset-bottom, 0px))" }}>
        <div className="aslide" style={{ background: "var(--card)", borderRadius: 24, padding: "24px 20px", margin: "0 16px", width: "100%" }}>
          <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 17, color: "var(--ink)", marginBottom: 8 }}>Cancel your {myReq.status === "accepted" ? "seat" : "request"}?</div>
          <div style={{ fontSize: 14, color: "var(--muted)", marginBottom: 24, whiteSpace: "pre-line" }}>
            {`Your seat on the trip to ${trip.to} will be cancelled.${myReq.status === "accepted" ? `\n\n${(() => { const h = (new Date(trip.datetime) - new Date()) / 3600000; return h >= 24 ? "Full refund (24h+ before trip)" : h >= 2 ? "50% refund (2–24h before trip)" : "No refund (under 2h before trip)"; })()}` : ""}`}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => { if (!cancelling) setCancelConfirm(false); }} disabled={cancelling} style={{ flex: 1, padding: "14px", borderRadius: 14, fontFamily: "Syne", fontWeight: 700, fontSize: 14, background: "var(--warm)", color: "var(--ink)", border: "none", cursor: cancelling ? "not-allowed" : "pointer", opacity: cancelling ? 0.5 : 1 }}>Keep my seat</button>
            <button disabled={cancelling} onClick={async () => {
              if (cancelling) return;
              setCancelling(true);
              try {
                const wasAccepted = myReq.status === "accepted";
                const reqId = myReq.id;
                await updateDoc(doc(db, "requests", reqId), { status: "cancelled" });
                if (wasAccepted) {
                  await updateDoc(doc(db, "trips", trip.id), { passengers: arrayRemove(currentUser.id), status: "active" });
                }
                setCancelConfirm(false);
                toast("Your seat has been cancelled.", "info");
                addDoc(collection(db, "notifications", trip.driverId, "items"), {
                  text: wasAccepted
                    ? `${currentUser.name} cancelled their confirmed seat on your trip to ${trip.to}.`
                    : `${currentUser.name} cancelled their seat request on your trip to ${trip.to}.`,
                  tab: "trips", subview: "driving",
                  type: wasAccepted ? "cancelled" : "request_withdrawn",
                  ts: serverTimestamp(), read: false,
                }).catch(() => {});
              } catch (e) {
                console.error("Cancel failed:", e);
                toast("Failed to cancel. Please try again.", "error");
              } finally {
                setCancelling(false);
              }
            }} style={{ flex: 1, padding: "14px", borderRadius: 14, fontFamily: "Syne", fontWeight: 700, fontSize: 14, background: "rgba(192,57,43,0.08)", color: "#c0392b", border: "1px solid rgba(192,57,43,0.15)", cursor: cancelling ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              {cancelling
                ? <span style={{ display: "flex", alignItems: "center", gap: 8 }}><span style={{ width: 14, height: 14, border: "2px solid #c0392b", borderTopColor: "transparent", borderRadius: "50%", animation: "igo-spin .7s linear infinite", display: "inline-block", flexShrink: 0 }}/>Cancelling…</span>
                : "Yes, cancel seat"}
            </button>
          </div>
        </div>
      </div>,
      document.body
    )}
    </>
  );
};

export default TripDetail;
